import { historyState, setHistoryState } from "./navigation-state.ts";

// goals:
//  - avoid manual garbage collection via refcounts and timeouts as much as possible
//    - we're still going to need this actually
//    - e.g. refreshing the timeline causing some posts to no longer be referenced.
//      if we do not refcount them they will persist in the history state and leak
//  - one source of truth that reactively updates all components making use of the data
//  - if possible, return nested objects directly instead of needing to mess with IDs

// idea:
//  - add feature to router for routes to persist objects in NavigationHistoryEntry.getState()
//    - we don't need to be the router to access [window.]navigation.currentEntry
//    - we can't store objects in there because theyre structurally-cloned
//    - need to keep track of our own navigation history
//  - have all objects add themselves there
//  - rely on browser for garbage collecting forwards navigation history

// though:
//  - we dont want to re-fetch the same data as we're navigating
//    (data from previous pages will still be useful, e.g. loading a post by clicking on it in the timeline)
//  - could recurse parent navigation entries?
//    - maybe with a limit of like 5 entries? (configurable?)

export class Cell<T> {
	#timeout: ReturnType<typeof setTimeout> | undefined;
	#value: T;

	setAt: number;
	refcount = 0;

	constructor(value: T) {
		this.#value = $state.raw(value);
		this.setAt = Date.now();
	}

	/** takes a reactive reference to this value, only applicable in a svelte context */
	ref(): T {
		$effect(() => {
			this.refcount += 1;

			if (this.#timeout) {
				if (import.meta.env.DEV) console.debug("[data storage] un-marking cell garbage", this);
				clearTimeout(this.#timeout);
				this.#timeout = undefined;
			}

			return () => {
				this.refcount -= 1;
			};
		});

		return this.#value;
	}

	get val(): T {
		return this.#value;
	}

	set val(value: T) {
		this.#value = value;
		this.setAt = Date.now();
	}

	/** internal */
	_markGarbage(afterMs: number, collector: () => void) {
		if (this.#timeout || this.refcount != 0) return;

		if (import.meta.env.DEV) console.debug("[data storage] marking cell garbage", this);
		this.#timeout = setTimeout(() => {
			if (this.refcount != 0) return;
			if (import.meta.env.DEV) console.debug("[data storage] collecting cell", this);
			collector();
		}, afterMs);
	}
}

export const getExisting = <T>(key: string): Cell<T> | undefined => {
	const entries = navigation.entries();

	// TODO: could be useful to copy/cache which a cell from a parent to the current entry
	// to make subsequent fetches faster

	entries.reverse();
	for (const entry of entries) {
		const state = historyState.get(entry.id);
		if (!state) continue;

		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
		const existing = state.get(key) as Cell<T> | undefined;
		if (existing == undefined) continue;

		if (import.meta.env.DEV)
			console.debug("[data storage] found cell at", { key, cell: existing, entry });
		return existing;
	}

	return undefined;
};

export const set = <T>(key: string, value: T): Cell<T> => {
	const entries = navigation.entries();

	for (const entry of entries) {
		const state = historyState.get(entry.key);
		if (!state) continue;

		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
		const existing = state.get(key) as Cell<T> | undefined;
		if (!existing) continue;

		if (import.meta.env.DEV)
			console.debug("[data storage] updating cell value at", { key, cell: existing, entry });
		existing.val = value;
		return existing;
	}

	const currentEntry = navigation.currentEntry!;
	const fresh = new Cell(value);
	if (import.meta.env.DEV)
		console.debug("[data storage] saving cell at", { key, cell: fresh, entry: currentEntry });
	setHistoryState(currentEntry.id, key, fresh);
	return fresh;
};

/* "page-state" that will not share data with previous navigations */
export const pageState = <T>(key: string, initial: T): Cell<T> => {
	const entryId = navigation.currentEntry!.id;
	const state = historyState.get(entryId);
	if (state) {
		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
		const existing = state.get(key) as Cell<T> | undefined;
		if (existing) {
			if (import.meta.env.DEV)
				console.debug("[data state] found cell at", { key, cell: existing, entry: entryId });
			return existing;
		}
	}

	const fresh = new Cell(initial);
	if (import.meta.env.DEV)
		console.debug("[data state] saving cell at", { key, cell: fresh, entry: entryId });
	setHistoryState(entryId, key, fresh);
	return fresh;
};

// we only want to sweep the current navigation entry
// future ones will presumably be GC'd by the browser calling onDispose on navigation-state.ts
// and past ones may still be navigated back to and should still be considered live
export const sweep = (afterMs: number) => {
	const state = historyState.get(navigation.currentEntry!.id);
	if (!state) return;

	if (import.meta.env.DEV) console.groupCollapsed("[data storage] sweeping");
	for (const [key, data] of state.entries()) {
		if (data instanceof Cell) {
			// oxlint-disable-next-line no-underscore-dangle
			data._markGarbage(afterMs, () => {
				state.delete(key);
			});
		}
	}
	if (import.meta.env.DEV) console.groupEnd();
};
